/**
 * ExecDIBDashboard — Configurable widget dashboard for DIB / state-level views.
 *
 * Reuses the same ExecDashboardProvider + DashboardContent as the PEO dashboard,
 * but creates a separate "DIB Dashboard" with geo-focused default widgets.
 */
import { useState, useEffect } from 'react'
import { ExecDashboardProvider } from '../contexts/ExecDashboardContext'
import { DashboardContent } from './ExecDashboard'
import { execAPI } from '../services/exec'
import type { GridLayoutItem } from '../types/exec'

const DIB_DASHBOARD_NAME = 'DIB Dashboard'

const DEFAULT_DIB_WIDGETS: { measure: string; config?: Record<string, unknown>; title?: string; w: number; h: number }[] = [
  {
    measure: 'geo_performance',
    config: { group_by: 'state', metrics: ['total_obligated'], chart_type: 'map' },
    title: 'DIB Spend by State',
    w: 6,
    h: 7,
  },
  {
    measure: 'geo_performance',
    config: { group_by: 'fiscal_year', metrics: ['total_obligated'], chart_type: 'line' },
    title: 'DIB Revenue Trend',
    w: 6,
    h: 5,
  },
  {
    measure: 'geo_performance',
    config: { group_by: 'psc_level1_code', metrics: ['total_obligated'], chart_type: 'bar', sort_by: '-total_obligated' },
    title: 'Top PSCs',
    w: 6,
    h: 5,
  },
  {
    measure: 'geo_performance',
    config: { group_by: 'state', metrics: ['award_count'], chart_type: 'bar', sort_by: '-award_count' },
    title: 'States by Award Count',
    w: 6,
    h: 5,
  },
]

export default function ExecDIBDashboard() {
  const [dashboardId, setDashboardId] = useState<string | null>(null)
  const [initializing, setInitializing] = useState(true)

  useEffect(() => {
    async function init() {
      try {
        const dashboards = await execAPI.getDashboards()
        const dibDash = dashboards.find((d) => d.name === DIB_DASHBOARD_NAME)

        if (dibDash) {
          setDashboardId(dibDash.id)
        } else {
          const newDash = await execAPI.createDashboard({
            name: DIB_DASHBOARD_NAME,
            is_default: false,
            layout: [],
          })
          const layout: GridLayoutItem[] = []
          for (let i = 0; i < DEFAULT_DIB_WIDGETS.length; i++) {
            const { measure, config, title, w, h } = DEFAULT_DIB_WIDGETS[i]
            const widget = await execAPI.addWidget(newDash.id, { measure, config, title })
            layout.push({
              i: widget.id,
              x: (i % 2) * 6,
              y: Math.floor(i / 2) * 7,
              w,
              h,
            })
          }
          await execAPI.updateLayout(newDash.id, layout)
          setDashboardId(newDash.id)
        }
      } catch (err) {
        console.error('Failed to initialize DIB dashboard:', err)
      } finally {
        setInitializing(false)
      }
    }
    init()
  }, [])

  if (initializing) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    )
  }

  return (
    // presetScope='dib' so the WidgetPicker on this dashboard surfaces only the
    // DIB/Geography presets — leadership presets stay on /exec/dashboard.
    <ExecDashboardProvider dashboardId={dashboardId} presetScope="dib">
      <DashboardContent />
    </ExecDashboardProvider>
  )
}
